import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { dragListKey, moveItem, t, type DragListKey, type DragListState } from '@bms2/shared';
import { Icon } from '../Icon';
import styles from './shared.module.css';

/**
 * The drag-and-drop list (piece 4): one module for the New View Modal's column
 * picker and Tile Canvas's edit mode. Keyboard reorder is not optional: Space
 * or Enter picks the focused item up, the arrows move it, Space drops it and
 * Escape puts it back. A pointer drag makes the same move. The parent owns
 * the order; the list reports each move it makes.
 */
export const dragListDocumentedStates = ['idle', 'grabbed'] as const;

export interface DragItem {
  id: string;
  label: string;
  /** A fixed item is listed but never moves and never has another item moved past it by the pointer. */
  fixed?: boolean;
}

export interface DragMove {
  id: string;
  from: number;
  to: number;
}

export interface DragListProps {
  items: readonly DragItem[];
  onReorder: (items: DragItem[], move: DragMove) => void;
  label: string;
  /** The harness's grabbed state: the index picked up when the list mounts. */
  initialGrabbed?: number;
  /** A control at an item's end: Selected Columns' remove (decision 2173). Its own keys never pick the item up. */
  trailing?: (item: DragItem) => ReactNode;
  /**
   * An item's own controls drawn in place of its label: a Discovery Choice option's text box (row T2840). An item
   * with a body is draggable only while its grip is held, so dragging across the box's text selects the text rather
   * than moving the item, as the item table's row does (decision 0649).
   */
  body?: (item: DragItem) => ReactNode;
}

const KEYS: ReadonlySet<string> = new Set([' ', 'Enter', 'ArrowUp', 'ArrowDown', 'Escape']);

export function DragList({ items, onReorder, label, initialGrabbed, trailing, body }: DragListProps) {
  const hintId = useId();
  const [grabbed, setGrabbed] = useState<number | null>(initialGrabbed ?? null);
  const [before, setBefore] = useState<DragItem[] | null>(initialGrabbed === undefined ? null : [...items]);
  const [focusId, setFocusId] = useState<string | null>(null);
  const dragFrom = useRef<number | null>(null);
  // The item whose grip the pointer holds; only consulted where items carry a body (decision 0649).
  const [armed, setArmed] = useState<string | null>(null);
  const refs = useRef(new Map<string, HTMLLIElement | null>());

  useEffect(() => {
    // 🔨 A control inside an item keeps its focus: a body's text box re-renders the list on each key (row T2840).
    const active = typeof document === 'undefined' ? null : document.activeElement;
    if (active instanceof HTMLElement && active.closest('[data-drag-item]') && !active.hasAttribute('data-drag-item')) return;
    if (focusId) refs.current.get(focusId)?.focus();
  }, [focusId, items]);

  const onKeyDown = (e: KeyboardEvent<HTMLLIElement>, index: number) => {
    // A key pressed on a control inside the item, such as its remove, is that control's own.
    if (e.target !== e.currentTarget) return;
    if (!KEYS.has(e.key)) return;
    if (items[index]?.fixed && grabbed === null) return;
    e.preventDefault();
    const state: DragListState<DragItem> = { items: [...items], grabbed, before };
    const result = dragListKey(state, e.key as DragListKey, index);
    if (result.state.items !== state.items) {
      const moved = result.state.grabbed !== null && grabbed !== null && result.state.grabbed !== grabbed;
      if (moved) {
        onReorder(result.state.items, { id: items[grabbed]!.id, from: grabbed, to: result.state.grabbed! });
      } else if (e.key === 'Escape' && before) {
        const id = items[grabbed!]!.id;
        onReorder(result.state.items, { id, from: grabbed!, to: before.findIndex((b) => b.id === id) });
      }
    }
    setGrabbed(result.state.grabbed);
    setBefore(result.state.before);
    const focusItem = result.state.items[result.focusIndex];
    if (focusItem) setFocusId(focusItem.id);
  };

  const onDrop = (to: number) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setArmed(null);
    if (from === null || from === to || items[from]?.fixed || items[to]?.fixed) return;
    onReorder(moveItem(items, from, to), { id: items[from]!.id, from, to });
  };

  /*
   * 🔒 The keyboard instructions are each item's spoken description, and they
   * draw on screen only while the list holds keyboard focus (decision 1323).
   * 💰 They stood under every reorder list and every tile arranger for people
   * using a mouse, who never need them (UI audit, 2026-09-29, rank 9).
   */
  return (
    <div className={styles.dragWrap}>
      <ol className={styles.dragList} aria-label={label} aria-describedby={hintId} data-drag-list="true">
        {items.map((item, index) => {
          const isGrabbed = grabbed === index;
          return (
            <li
              key={item.id}
              ref={(el) => {
                refs.current.set(item.id, el);
              }}
              tabIndex={item.fixed ? -1 : 0}
              aria-describedby={hintId}
              draggable={!item.fixed && (!body || armed === item.id)}
              className={styles.dragItem}
              data-drag-item={item.id}
              data-grabbed={isGrabbed ? 'true' : 'false'}
              data-fixed={item.fixed ? 'true' : 'false'}
              onKeyDown={(e) => onKeyDown(e, index)}
              onDragStart={() => {
                dragFrom.current = index;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={() => setArmed(null)}
              onPointerUp={() => setArmed(null)}
              onDrop={(e) => {
                e.preventDefault();
                onDrop(index);
              }}
            >
              <span className={styles.dragHandle} aria-hidden="true" data-drag-handle={item.id} onPointerDown={body ? () => setArmed(item.id) : undefined}>
                <Icon meaning={item.fixed ? 'locked' : 'dragHandle'} size="inline" />
              </span>
              {body ? <span className={styles.dragBody}>{body(item)}</span> : <span className={styles.dragLabel}>{item.label}</span>}
              {trailing ? trailing(item) : null}
              {isGrabbed ? (
                <span className={styles.dragStatus} role="status">
                  {t('drag.grabbed')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p id={hintId} className={styles.dragHint} data-drag-hint="true">
        {t('drag.instructions')}
      </p>
    </div>
  );
}
